import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { Mail, Phone, MapPin, Send } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Message sent! I\'ll get back to you soon.');
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error('Failed to send message. Please try again or email directly.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-20" data-testid="contact-page">
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h1 className="text-5xl md:text-6xl font-heading font-light tracking-tight text-foreground mb-6" data-testid="contact-heading">
              Get in <span className="italic text-primary">Touch</span>
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Have questions or ready to start your therapy journey? I'd love to hear from you.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Form */}
            <Card className="bg-white/80 backdrop-blur-sm rounded-3xl p-8">
              <h2 className="text-2xl font-heading font-medium text-foreground mb-6">Send a Message</h2>
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div>
                  <Label htmlFor="name" className="text-foreground font-medium">Name</Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="mt-2 bg-white/50 border-muted focus:border-primary rounded-lg"
                    placeholder="Your name"
                    data-testid="contact-name-input"
                  />
                </div>

                <div>
                  <Label htmlFor="email" className="text-foreground font-medium">Email</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="mt-2 bg-white/50 border-muted focus:border-primary rounded-lg"
                    placeholder="your@email.com"
                    data-testid="contact-email-input"
                  />
                </div>

                <div>
                  <Label htmlFor="phone" className="text-foreground font-medium">Phone</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    className="mt-2 bg-white/50 border-muted focus:border-primary rounded-lg"
                    placeholder="+91 98765 43210"
                    data-testid="contact-phone-input"
                  />
                </div>

                <div>
                  <Label htmlFor="message" className="text-foreground font-medium">Message</Label>
                  <Textarea
                    id="message"
                    name="message"
                    required
                    value={formData.message}
                    onChange={handleChange}
                    className="mt-2 bg-white/50 border-muted focus:border-primary rounded-lg"
                    placeholder="Tell me about what you're looking for..."
                    rows={6}
                    data-testid="contact-message-textarea"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-primary text-white hover:bg-primary/90 rounded-full py-6 text-lg"
                  data-testid="contact-submit-button"
                >
                  {loading ? 'Sending...' : (
                    <>
                      Send Message <Send className="ml-2" size={20} />
                    </>
                  )}
                </Button>
              </form>
            </Card>

            {/* Contact Information */}
            <div className="space-y-8">
              <Card className="bg-white/80 backdrop-blur-sm rounded-3xl p-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <Mail className="text-primary" size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-heading font-medium text-foreground mb-2">Email</h3>
                    <a href="mailto:therapywithanvitasethi@gmail.com" className="text-muted-foreground hover:text-primary transition-colors">
                      therapywithanvitasethi@gmail.com
                    </a>
                  </div>
                </div>
              </Card>

              <Card className="bg-white/80 backdrop-blur-sm rounded-3xl p-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <svg className="text-primary" width="24" height="24" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-xl font-heading font-medium text-foreground mb-2">Instagram</h3>
                    <a href="https://instagram.com/therapywithanvita" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-primary transition-colors">
                      @therapywithanvita
                    </a>
                  </div>
                </div>
              </Card>

              <Card className="bg-white/80 backdrop-blur-sm rounded-3xl p-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <svg className="text-primary" width="24" height="24" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M0 0v24h24V0H0zm19.938 5.686L18.651 6.92a.376.376 0 0 0-.143.362v9.067a.376.376 0 0 0 .143.361l1.257 1.234v.271h-6.322v-.27l1.302-1.265c.128-.128.128-.165.128-.36V8.99l-3.62 9.195h-.49L6.69 8.99v6.163a.85.85 0 0 0 .233.707l1.694 2.054v.271H3.815v-.27L5.51 15.86a.82.82 0 0 0 .218-.707V8.027a.624.624 0 0 0-.203-.527L4.019 5.686v-.27h4.674l3.613 7.923 3.176-7.924h4.456v.271z"/>
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-xl font-heading font-medium text-foreground mb-2">Medium</h3>
                    <a href="https://medium.com/@therapywithanvitasethi" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-primary transition-colors">
                      @therapywithanvitasethi
                    </a>
                  </div>
                </div>
              </Card>

              <Card className="bg-white/80 backdrop-blur-sm rounded-3xl p-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <svg className="text-primary" width="24" height="24" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-xl font-heading font-medium text-foreground mb-2">LinkedIn</h3>
                    <a href="https://linkedin.com/in/anvita-sethi-b96431103" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-primary transition-colors">
                      anvita-sethi-b96431103
                    </a>
                  </div>
                </div>
              </Card>

              <Card className="bg-white/80 backdrop-blur-sm rounded-3xl p-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <MapPin className="text-primary" size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-heading font-medium text-foreground mb-2">Location</h3>
                    <p className="text-muted-foreground">
                      Online Sessions Available<br />
                      Serving Indian clients across the globe
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="bg-primary text-white rounded-3xl p-8">
                <h3 className="text-2xl font-heading font-medium mb-4">Session Hours</h3>
                <div className="space-y-2 text-white/90">
                  <p>Tuesday – Friday: 10:00 AM – 6:00 PM IST</p>
                  <p>Saturday: 10:00 AM – 4:00 PM IST</p>
                  <p>Sunday & Monday: Closed</p>
                </div>
              </Card>

              <Card className="bg-accent text-white rounded-3xl p-8">
                <h3 className="text-2xl font-heading font-medium mb-3">Community</h3>
                <p className="text-white/90 mb-4">
                  Co-founder of <strong>UNTAMED</strong> - A body image community for women
                </p>
                <a 
                  href="https://untamedcommunity.org" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-block bg-white text-accent px-6 py-2 rounded-full font-medium hover:bg-white/90 transition-colors"
                >
                  Visit UNTAMED
                </a>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
